import type { Subject } from './types'

export type Tier = 'exam' | 'everyday'
export type HookType = 'example' | 'contrast' | 'formula' | 'memory'
export interface Flashcard {
  id: string
  subject: Subject
  term: string
  definition: string
  /** The book's own wording, kept only where the authored definition reads materially differently. */
  bookWording?: string
  hook?: string
  hookType?: HookType
  tier: Tier
  /** Past papers that asked for this definition, and the marks it was worth. */
  asked: number
  marks?: number
  section: string
  chapter: string
  pdfPage: number
  printedPage: string
}
export interface FlashcardDeck { version: string; cards: Flashcard[] }

/** b: box (0 = just forgotten), d: local day number it is next due, l: times forgotten. */
export interface CardRecord { b: number; d: number; l: number }
export type FlashcardProgress = Record<string, CardRecord>
export type CardStatus = 'new' | 'forgotten' | 'due' | 'learned'
export type SessionMode = 'review' | 'hard' | 'all'

export const PROGRESS_KEY = 'margin:flashcards'
/** Days until a card returns after being known 1, 2, 3… times in a row. */
const INTERVALS = [1, 3, 7, 16, 35]
/** A forgotten card comes back after this many other cards in the same session. */
const RETRY_GAP = 4
/** Cards stay in "ones I keep forgetting" until they have been known this many times in a row. */
const HARD_UNTIL_BOX = 3

export const dayNumber = (now = new Date()) => Math.floor((now.getTime() - now.getTimezoneOffset() * 60000) / 86400000)

export function cardStatus(record: CardRecord | undefined, today: number): CardStatus {
  if (!record) return 'new'
  if (record.b === 0) return 'forgotten'
  return record.d <= today ? 'due' : 'learned'
}

export function gradeCard(record: CardRecord | undefined, knew: boolean, today: number): CardRecord {
  if (!knew) return { b: 0, d: today, l: (record?.l ?? 0) + 1 }
  const b = Math.min((record?.b ?? 0) + 1, INTERVALS.length)
  return { b, d: today + INTERVALS[b - 1], l: record?.l ?? 0 }
}

export function shuffled<T>(items: T[], random = Math.random): T[] {
  const copy = [...items]
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1))
    ;[copy[i], copy[j]] = [copy[j], copy[i]]
  }
  return copy
}

export function isHard(record: CardRecord | undefined) {
  return !!record && record.l > 0 && record.b < HARD_UNTIL_BOX
}

/** Picks a session's cards: what she has forgotten first, then what is due, then new cards. */
export function pickSession(cards: Flashcard[], progress: FlashcardProgress, mode: SessionMode, size: number, today: number, random = Math.random): string[] {
  const shuffle = (list: Flashcard[]) => shuffled(list, random)
  let ordered: Flashcard[]
  if (mode === 'all') ordered = shuffle(cards)
  else if (mode === 'hard') ordered = shuffle(cards.filter(card => isHard(progress[card.id]))).sort((a, b) => progress[b.id].l - progress[a.id].l)
  else {
    const group = (status: CardStatus) => shuffle(cards.filter(card => cardStatus(progress[card.id], today) === status))
    // New cards that examiners have actually asked for are worth learning first.
    const fresh = group('new').sort((a, b) => Number(b.asked > 0) - Number(a.asked > 0))
    ordered = [...group('forgotten'), ...group('due'), ...fresh]
  }
  return ordered.slice(0, size).map(card => card.id)
}

/** The queue after answering its first card: known cards leave, forgotten ones return shortly. */
export function advanceQueue(queue: string[], knew: boolean): string[] {
  const [current, ...rest] = queue
  if (knew || current === undefined) return rest
  const at = Math.min(RETRY_GAP, rest.length)
  return [...rest.slice(0, at), current, ...rest.slice(at)]
}

export function isValidProgress(value: unknown): value is FlashcardProgress {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  return Object.entries(value).every(([id, record]) => {
    const { b, d, l } = (record ?? {}) as Partial<CardRecord>
    return id.length <= 180 && [b, d, l].every(number => Number.isInteger(number) && (number as number) >= 0)
  })
}

export function readProgress(text: string | null): FlashcardProgress {
  try {
    const value: unknown = JSON.parse(text ?? '{}')
    return isValidProgress(value) ? value : {}
  } catch {
    return {}
  }
}

/** Hides the term's own words so a definition-first card does not give away its answer. */
export function maskTerm(definition: string, term: string) {
  const words = term.replace(/\(.*?\)/g, ' ').split(/[^\p{L}\p{N}-]+/u).filter(word => word.length > 3)
  return words.reduce((text, word) => text.replace(new RegExp(`\\b${word.replace(/s$/i, '').replace(/[-]/g, '\\-')}\\w*`, 'gi'), '____'), definition).replace(/____(\s+____)+/g, '____')
}

/** Book headers are printed in capitals; "THE MARKET SYSTEM" reads better as "The market system". */
export function chapterLabel(text: string) {
  const lower = text.toLowerCase().replace(/^\d+\s+/, '')
  return lower.charAt(0).toUpperCase() + lower.slice(1)
}
